/**
 * GMap.jsx
 * Created by michaelbray on 2/23/18.
 */

import PropTypes from 'prop-types';
import React from 'react';
import { browserHistory } from 'react-router';
import shallowEqual from 'fbjs/lib/shallowEqual';

const DEFAULT_ZOOM = 10;

const propTypes = {
    searchCenter: PropTypes.objectOf(PropTypes.number),
    locationData: PropTypes.object,
    highlightUpdate: PropTypes.func,
    showOverlayImages: PropTypes.bool
};

/*
 * Note regarding all google references being this.google:
 * Had to assign google ref to an JS class var so that in the jest tests
 * I could mock over it and write fake functions so the rest of the class
 * could still be tested.
 */
export default class GMap extends React.Component {
    /* eslint-disable no-undef */
    componentWillMount() {
        this.google = google;
    }
    /* eslint-enable no-undef */

    shouldComponentUpdate(nextProps) {
        if (!nextProps.locationData || nextProps.locationData.length === 0) {
            return false;
        }
        if (this.infoWindow !== undefined && this.props.locationData && shallowEqual(this.props.locationData, nextProps.locationData)) {
            return false;
        }

        return true;
    }

    componentDidUpdate() {
        const locatDat = this.props.locationData;
        if (locatDat !== undefined && locatDat.length > 0 && locatDat[0].addresses) {
            if (!this.map) {
                this.initMap();
            }

            this.configureMap();
        }
    }

    setMapStyle() {
        this.map.data.setStyle((feature) => {
            return {
                title: feature.getProperty('name'),
                optimized: false
            };
        });
    }

    // permaMarker is necessary to persist a marker obj to tie the infoWindow to.
    // All other markers have their maps wiped on re-render.
    initMarkers() {
        if (this.markers && this.markers.length > 0) {
            this.markers.map((feature) => feature.setMap(null));
        }

        const bounds = new this.google.maps.LatLngBounds();
        this.markers = [];

        if (this.props.locationData) {
            this.props.locationData.forEach((location) => {
                const newMarker = this.createMarker(location.facility_latitude, location.facility_longitude);
                this.markers.push(newMarker);
                bounds.extend(newMarker.position);
                newMarker.addListener('click', () => {
                    if (this.infoWindow) {
                        this.infoWindow.setContent(this.assembleInfoContent(location));
                        this.permaMarker.setPosition(newMarker.getPosition());
                        this.infoWindow.open(this.map, this.permaMarker);
                        if (document.getElementById('searchresults_page_div') && document.getElementById('campground_link')) {
                            this.google.maps.event.addDomListener(document.getElementById('campground_link'), 'click', this.PageRedirect);
                        }
                    }
                    if (this.props.highlightUpdate) {
                        this.props.highlightUpdate(location.facility_id);
                    }
                });
            });

            if (this.props.locationData.length === 1) {
                this.map.setZoom(DEFAULT_ZOOM);
                this.map.setCenter(this.mapCenter());
            }
            if (this.props.locationData.length > 1) {
                this.map.fitBounds(bounds);
                this.map.panToBounds(bounds);
            }

            // For some reason trying to attach this listener earlier in places like
            // componentDidMount always failed (couldn't select the infoWindow on creation for some reason).
            // So it gets attached here.
            if (document.getElementById('searchresults_page_div') && document.getElementById('campground_link')) {
                this.google.maps.event.addDomListener(document.getElementById('campground_link'), 'click', this.PageRedirect);
            }
        }
    }

    initCenterMarker() {
        if (this.props.searchCenter) {
            this.centerMarker = new this.google.maps.Marker({
                position: {
                    lat: this.props.searchCenter.lat,
                    lng: this.props.searchCenter.lng
                },
                icon: {
                    url: `${process.env.CDN}/images/area-center-circle.png`,
                    scaledSize: new this.google.maps.Size(14, 14)
                },
                draggable: false,
                map: this.map
            });
        }
    }

    /* eslint-disable no-undef */
    createMap() {
        const mapOptions = {
            zoom: DEFAULT_ZOOM,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            center: this.mapCenter()
        };

        return new this.google.maps.Map(this.mapCanvas, mapOptions);
    }
    /* eslint-enable no-undef */

    componentDidUnmount() {
        this.google.maps.event.clearListeners(this.map, 'zoom_changed');
    }

    initMap() {
        // create the map, marker and infoWindow after the component has
        // been rendered because we need to manipulate the DOM for Google =(
        if (this.map === undefined) {
            this.map = this.createMap();
        }

        this.permaMarker = this.createMarker(0, 0);

        if (this.infoWindow === undefined) {
            this.createInfoWindow();
        }

        if (this.props.locationData !== undefined && this.props.locationData.length > 0) {
            this.configureMap();
        }
    }

    mapCenter() {
        let lat = 38.9072; // Default map to Washington DC
        let lng = -77.0369;
        if (this.props.locationData !== undefined && this.props.locationData.length > 0) {
            lat = this.props.locationData[0].facility_latitude;
            lng = this.props.locationData[0].facility_longitude;
        }

        return new this.google.maps.LatLng(
            lat, lng
        );
    }

    forceResize() {
        const evt = document.createEvent('UIEvents');
        evt.initUIEvent('resize', true, false, window, 0);
        window.dispatchEvent(evt);
    }

    createMarker(lat, lng) {
        return new this.google.maps.Marker({
            position: new this.google.maps.LatLng(lat, lng),
            map: this.map
        });
    }

    createInfoWindow() {
        const contentString = '<div id="info_window"></div>';
        this.infoWindow = new this.google.maps.InfoWindow({
            content: contentString
        });
    }

    configureMap() {
        this.setMapStyle();
        this.initMarkers();
        this.forceResize(); // This forces the map to rerender to prevent size/loading issues.
        this.initCenterMarker();
    }

    // The <Link> contained in this code isn't actually used, it just holds a 'to' attribute
    // which is a handy place to story the relevant url redirect info for the getAttribute call.
    /* eslint-disable prefer-template */
    assembleInfoContent(location) {
        const pageSelector = document.getElementById('searchresults_page_div');
        let contentString = '<div id="info_window" >';

        if (this.props.showOverlayImages) {
            if (location.media && location.media.length > 0) {
                contentString = contentString.concat('<img src="' + location.media[0].url + '" alt="' + location.media[0].title + '" />');
            }
        }

        if (location.facility_id && location.facility_name && pageSelector) {
            contentString = contentString.concat(
                '<div className="text-inline m_T m_B-0 p_R-2x">' + location.facility_name + '</div>' +
                '<Link id="info_redirect" to="/campgrounds/' + location.facility_id + '">' +
                '<a href="#' + location.facility_id + '_div" className="text-inline m_T m_B-0 p_R-2x">Show details</a></Link><br/>' +
                '<a id="campground_link">Go to campground page</a><br/>');
        }

        if (location.addresses && location.addresses.length > 0) {
            contentString = contentString.concat('<span>Part of ' + location.addresses[0].city + ', ' + location.addresses[0].address_state_code + '</span>');
        }

        if (location.campsites_count) {
            contentString = contentString.concat('<span> | ' + location.campsites_count + ' Available Sites</span>');
        }

        contentString = contentString.concat('</div>');

        return contentString;
    }
    /* eslint-enable prefer-template */

    PageRedirect() {
        browserHistory.push(document.getElementById('info_redirect').getAttribute("to"));
    }

    render() {
        return (
            <div className="GMap">
                <div
                    className="GMap-canvas"
                    ref={(div) => {
                        this.mapCanvas = div;
                    }} />
            </div>
        );
    }
}

GMap.propTypes = propTypes;
